import React from "react";
import { toast } from "sonner";
import { clsx } from "clsx";
import { useTranslation } from "react-i18next";
import { useLocale } from "@/hooks/useLocale";
import { ShareIcon, FileDown } from "lucide-react";
import { ChartHeader } from "@/components/ChartHeader";
import { Card, Tabs, TabsList, TabsTrigger, Skeleton } from "@/components/ui";
import { FunnelBarData } from "@/components/Funnel/types";
import { useProtectedNavigation } from "@/routes/useProtectedNavigation";
import { useOrgPermissions } from "@/hooks/useOrgPermissions";
import {
  OrganizationPartnershipLevelEnum,
  StudentTypeEnum,
} from "@/__generated__/types";
import { useDialogContext } from "@/components/DialogProvider";
import { useRoute } from "@/routes/getRoute";
import { cn } from "@/lib/utils";
import { exportChartCSV, timestampFilename } from "@/util/exportChartCSV";
import { useBuildUrlState } from "@/hooks/useUrlSerializer";
import { useCurrentCohortYear } from "@/hooks/useCurrentCohortYear";
import { FilterState } from "../FilterSidebar/FilterState";

export type Tab = "inCommunity" | "notInCommunity";
interface Props {
  inCommunityRows: FunnelBarData[];
  notInCommunityRows: FunnelBarData[];
  actualDataDate: Date;
  loading: boolean;
  tab: Tab;
  onChangeTab: (tab: Tab) => void;
}

const BAR_HEIGHT = 40;
const WEDGE_HEIGHT = 16;
const BORDER_RADIUS = 6;
const SKELETON_ROWS = 5;

export const HalfFunnelChart: React.FC<Props> = ({
  inCommunityRows,
  notInCommunityRows,
  actualDataDate,
  loading,
  tab,
  onChangeTab,
}) => {
  const { t } = useTranslation();
  const { navToRoute } = useProtectedNavigation();
  const { showPermissionDialog } = useDialogContext();
  const getRoute = useRoute();
  const { hasMinOrgPartnershipLevel } = useOrgPermissions();

  const { cohortYear } = useCurrentCohortYear();

  const funnelDefaultQueryParams = useBuildUrlState("FUNNEL_CHART")({
    compareFilterState: undefined,
    filterState: new FilterState({
      studentType: StudentTypeEnum.FirstYear,
      cohortYear: cohortYear ? [`${cohortYear}`] : undefined,
    }),
  });

  const displayRows =
    tab === "inCommunity" ? inCommunityRows : notInCommunityRows;

  const onShare = () => {
    const hasProAccess = hasMinOrgPartnershipLevel(
      OrganizationPartnershipLevelEnum.Pro,
    );

    if (hasProAccess) {
      navigator.clipboard.writeText(
        `${window.location.protocol}//${window.location.hostname}${getRoute("funnel")}`,
      );
      toast(t("global.shareLinks.linkCopied"));
    } else {
      showPermissionDialog();
    }
  };

  const handleExportCSV = () => {
    if (
      (!inCommunityRows || inCommunityRows.length === 0) &&
      (!notInCommunityRows || notInCommunityRows.length === 0)
    ) {
      toast.error("No data available to export");
      return;
    }

    try {
      const inCommunityCountHeader = t(
        "zeemeeFunnel.csvHeaders.inCommunityCount",
      );
      const notInCommunityCountHeader = t(
        "zeemeeFunnel.csvHeaders.notInCommunityCount",
      );

      const allStages = new Set([
        ...inCommunityRows.map((row) => row.stage),
        ...notInCommunityRows.map((row) => row.stage),
      ]);

      const exportData = Array.from(allStages).map((stage) => {
        const inCommunityRow = inCommunityRows.find(
          (row) => row.stage === stage,
        );
        const notInCommunityRow = notInCommunityRows.find(
          (row) => row.stage === stage,
        );

        return {
          "": t(`funnelGroups.${stage}`),
          [inCommunityCountHeader]: inCommunityRow ? inCommunityRow.count : "",
          [notInCommunityCountHeader]: notInCommunityRow
            ? notInCommunityRow.count
            : "",
        };
      });

      const headers = ["", inCommunityCountHeader, notInCommunityCountHeader];

      exportChartCSV({
        filename: timestampFilename("half-funnel-chart"),
        headers,
        data: exportData,
      });

      toast.success(t("global.exportToCSV.success"));
    } catch (error) {
      console.error("Error exporting CSV:", error);
      toast.error(t("global.exportToCSV.failure"));
    }
  };

  return (
    <Card>
      <ChartHeader
        title={t("zeemeeFunnel.chart.inCommunityVsNotInCommunity")}
        subtitle={t("zeemeeFunnel.chart.studentsByFunnelState", {
          date: actualDataDate.toLocaleDateString("en-US", {
            month: "long",
            day: "numeric",
            year: "numeric",
          }),
        })}
        menuOptions={[
          {
            label: t("global.exportToCSV.menuTitle"),
            onClick: handleExportCSV,
            icon: <FileDown aria-hidden height={16} />,
          },
          {
            label: t("zeemeeFunnel.chart.share"),
            onClick: onShare,
            icon: <ShareIcon aria-hidden height={16} />,
          },
        ]}
      />

      <div className="flex gap-2 px-6 py-2">
        <Tabs
          value={tab}
          onValueChange={(value) => {
            onChangeTab(value as Tab);
          }}
        >
          <TabsList>
            <TabsTrigger value="inCommunity">
              {t("zeemeeFunnel.chart.inCommunity")}
            </TabsTrigger>
            <TabsTrigger value="notInCommunity">
              {t("zeemeeFunnel.chart.notInCommunity")}
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      <div
        className="relative cursor-pointer px-6 pt-2 pb-6"
        onClick={() => navToRoute("funnel", {}, funnelDefaultQueryParams)}
      >
        <div className="relative mr-[25%] flex">
          {loading || !displayRows || !displayRows.length ? (
            <SkeletonRows count={SKELETON_ROWS} />
          ) : (
            <>
              <div className="mr-6">
                <StageLabelsColumn stages={displayRows} />
              </div>
              <div className={clsx("relative z-1 flex-grow animate-fade-in")}>
                <Chart stages={displayRows} index={0} tab={tab} />
              </div>
              <div className="w-15"></div>
              <div className="absolute top-0 right-0 bottom-0 left-0">
                <GridLines count={displayRows.length - 1} />
              </div>
            </>
          )}
        </div>
      </div>
    </Card>
  );
};

const Badge = ({
  percent,
  count,
  className,
  percentClassName,
}: {
  percent: number | undefined;
  count: number;
  className: string;
  percentClassName: string;
}) => {
  const locale = useLocale();
  return (
    <div className={cn("text-[10px] font-bold", className)}>
      <div className={cn("rounded px-1 py-[1px]", percentClassName)}>
        {percent !== undefined ? `${percent}%` : "-"}
      </div>
      <div className="pt-[1px] text-center">
        {new Intl.NumberFormat(locale).format(count)}
      </div>
    </div>
  );
};

const StageLabelsColumn = ({ stages }: { stages: FunnelBarData[] }) => {
  const { t } = useTranslation();
  return stages.map((data, index) => {
    const isFirst = index === 0;
    return (
      <div
        key={index}
        className="flex items-end pb-1 text-sm font-medium capitalize"
        style={{
          height: isFirst ? BAR_HEIGHT : BAR_HEIGHT + WEDGE_HEIGHT,
        }}
      >
        {t("funnelGroups." + data.stage)}
      </div>
    );
  });
};

const GridLines = ({ count }: { count: number }) => {
  if (count < 0) return;
  return (
    <>
      {new Array(count).fill(null).map((_, idx) => {
        const isFirst = idx === 0;
        return (
          <div
            key={idx}
            className={`border-b border-border`}
            style={{
              height: isFirst ? BAR_HEIGHT : BAR_HEIGHT + WEDGE_HEIGHT,
            }}
          ></div>
        );
      })}
    </>
  );
};

const Chart = ({
  stages,
  index,
  tab,
}: {
  stages: FunnelBarData[];
  index: number;
  tab: string;
}) => {
  if (index === stages.length) return;
  const { stage, count, percent } = stages[index];
  const isLast = index === stages.length - 1;
  const nextPercentage = isLast ? 0 : stages[index + 1].percent || 0;

  return (
    <div key={stage} className="flex">
      <div
        style={{ width: `${percent || 0}%`, minWidth: "1px" }}
        className="transition-all duration-300"
      >
        <div
          className={cn(
            `h-[${BAR_HEIGHT}px] w-full rounded-r-md bg-funnel-half`,
            tab === "inCommunity" ? "bg-funnel-1-left" : "bg-funnel-1-right",
          )}
        ></div>
        {!isLast && (
          <div
            style={{
              width: "100%",
              height: `${WEDGE_HEIGHT}px`,
              clipPath: `polygon(0 0, calc(100% - ${BORDER_RADIUS}px) 0, calc(${nextPercentage}% - ${BORDER_RADIUS}px) 100%, 0 100%)`,
            }}
            className={cn(
              "h-4 w-full bg-funnel-half-wedge/12 transition-all duration-300",
              tab === "inCommunity"
                ? "bg-funnel-1-left-wedge"
                : "bg-funnel-1-right-wedge",
            )}
          ></div>
        )}
        <Chart stages={stages} index={index + 1} tab={tab} />
      </div>

      <div className="relative">
        <div className="absolute top-1 left-2">
          <Badge
            percent={percent}
            count={count}
            className={
              tab === "inCommunity"
                ? "text-funnel-1-left"
                : "text-funnel-1-percent-right"
            }
            percentClassName={
              tab === "inCommunity"
                ? "border border-funnel-1-left bg-funnel-1-left-wedge"
                : "px-[5px] py-[2px]"
            }
          />
        </div>
      </div>
    </div>
  );
};

const SkeletonRows = ({ count }: { count: number }) => {
  let widthPercent = 2;
  return (
    <div className="w-full">
      {new Array(count).fill(null).map((_, idx) => {
        widthPercent *= 0.5;
        return (
          <div key={idx} className="flex">
            <Skeleton className="mr-6 h-[40px] w-19" />
            <div className="flex-grow">
              <Skeleton
                className="h-[40px]"
                style={{ width: `${widthPercent * 100}%` }}
              />
              {idx !== count - 1 && <div className="h-4 w-full"></div>}
            </div>
          </div>
        );
      })}
    </div>
  );
};
